import { Module } from 'vuex';
import {
  action,
  actionCreatorFactory,
  combineAction,
  combineMutation,
  mutation,
} from 'vuex-typescript-fsa/lib';
import { RootState } from '@/store';
import {
  CoverLetter,
  CoverLetterResponse,
  CreateCoverLetterRequest,
} from '@/../gen';
import { UpdateCoverLetterRequest } from '@/../gen/api';

export const namespace = 'coverLetters';
const actionCreator = actionCreatorFactory(namespace);

interface CoverLetterParams {
  id: number;
}

export const GetCoverLetters = actionCreator<null>('GET_COVER_LETTERS');
export const SetCoverLetters = actionCreator<CoverLetterResponse[]>(
  'SET_COVER_LETTERS'
);
export const GetCoverLetter = actionCreator<CoverLetterParams>(
  'GET_COVER_LETTER'
);
export const SetCoverLetter = actionCreator<CoverLetterResponse>(
  'SET_COVER_LETTER'
);
export const AddCoverLetter = actionCreator<CoverLetterResponse>(
  'ADD_COVER_LETTER'
);
export const CreateCoverLetter = actionCreator<{
  body: CreateCoverLetterRequest;
}>('CREATE_COVER_LETTER');
export const UpdateCoverLetter = actionCreator<{
  params: CoverLetterParams;
  body: UpdateCoverLetterRequest;
}>('UPDATE_COVER_LETTER');
export const DeleteCoverLetter = actionCreator<{ params: CoverLetterParams }>(
  'DELETE_COVER_LETTER'
);

export const ResetState = actionCreator<null>('RESET_STATE');

export interface State {
  coverLetters: CoverLetter[];
  coverLetter: CoverLetter;
}

const initialState = (): State => {
  return { coverLetters: [], coverLetter: null };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: initialState,
  actions: combineAction(
    action(GetCoverLetters, async function(context) {
      const { data } = await this.proffApi.listCoverLetters();
      context.commit(SetCoverLetters(data.data));
    }),
    action(GetCoverLetter, async function(context, action) {
      const { data } = await this.proffApi.getCoverLetter(action.payload.id);
      context.commit(SetCoverLetter(data.data));
    }),
    action(CreateCoverLetter, async function(context, action) {
      const { data } = await this.proffApi.createCoverLetter(
        action.payload.body
      );
      context.commit(AddCoverLetter(data.data));
    }),
    action(UpdateCoverLetter, async function(context, action) {
      const { params, body } = action.payload;
      const { data } = await this.proffApi.updateCoverLetter(params.id, body);
      context.commit(SetCoverLetter(data.data));
    }),
    action(DeleteCoverLetter, async function(context, action) {
      const { params } = action.payload;
      await this.proffApi.deleteCoverLetter(params.id);
      context.commit(SetCoverLetter(null));
    }),
    action(ResetState, function(context, action) {
      context.commit(ResetState(action.payload));
    })
  ),
  mutations: combineMutation(
    mutation(SetCoverLetters, (state, action) => {
      state.coverLetters = action.payload.map(item => item.attributes);
    }),
    mutation(AddCoverLetter, (state, action) => {
      state.coverLetters.push(action.payload?.attributes);
    }),
    mutation(SetCoverLetter, (state, action) => {
      state.coverLetter = action.payload?.attributes;
    }),
    mutation(ResetState, state => {
      Object.assign(state, initialState());
    })
  ),
  getters: {
    coverLetters: state => state.coverLetters,
    coverLetter: state => state.coverLetter,
  },
};
